import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Form } from "react-bootstrap";
import { Col, Container, Row, Input, Label, Spinner } from "reactstrap";
import { contactUs } from "../../api/contacts"; // Reusing your contact api function
// Import Images
import deleteImage from "../../assets/images/contact.png"; // Replace with a custom illustration if available
import { CustomSnackbar } from "../../helpers/CustomSnackbar";
import { isValidMobile } from "../../helpers/common";

const DeleteAccount = () => {
  const [loading, setLoading] = useState(false);
  const initialState = {
    name: "",
    emailAddress: "",
    contactNumber: "",
    subject: "Delete my Webvi account",
    message: "",
  };

  const [formData, setFormData] = useState(initialState);

  const onChangeValue = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const onNumberInputChange = (e) => {
    const inputValue = e.target.value;
    if (!inputValue) {
      setFormData({ ...formData, [e.target.name]: "" });
      return;
    }
    if (!/^\d+$/.test(inputValue)) return;
    setFormData({ ...formData, [e.target.name]: inputValue });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) { CustomSnackbar.warning("Name is required!"); return; }
    if (!formData.emailAddress) { CustomSnackbar.warning("Registered email address is required!"); return; }
    if (formData.contactNumber && !isValidMobile(formData.contactNumber)) {
      CustomSnackbar.warning("Please enter valid mobile number"); return;
    }
    if (!formData.message) { CustomSnackbar.warning("Please provide a brief reason for deletion."); return; }

    setLoading(true);
    contactUs({ 
      ...formData, 
      subject: "Delete my Webvi account",
      AdditionalFields: { request_type: "account_deletion" } 
    })
      .then(() => {
        setFormData(initialState);
        CustomSnackbar.success("Account deletion request submitted successfully. We will verify and process it within 2 working days.");
      })
      .catch((err) => {
        CustomSnackbar.error(err?.message || "Error occurred while submitting request");
      })
      .finally(() => setLoading(false));
  };

  return (
    <React.Fragment>
      <Helmet>
        <title>Delete Account Request | Webvi</title>
      </Helmet>
      
      <section className="section py-5">
        <Container>
          <Row className="align-items-center mt-4">

            {/* LEFT SIDE: Instructions & Guidelines */}
            <Col lg={5}>
              <div className="text-center mb-4">
                <img src={deleteImage} alt="Delete Account" className="img-fluid" style={{ maxHeight: "250px" }} />
              </div>
              <div className="bg-light p-4 rounded-4 border">
                <h5 className="text-dark fw-bold mb-3">How account deletion works</h5>
                <ul className="text-muted fs-13 ps-3 mb-0" style={{ lineHeight: "1.8" }}>
                  <li><strong>Verification:</strong> Submit using your registered details. We may contact you to verify your identity.</li>
                  <li><strong>What gets deleted:</strong> Your profile, resume, saved jobs, and applications held within Webvi.</li>
                  <li><strong>What is retained:</strong> Payment, invoice, and GST records legally required for tax/compliance, plus fraud prevention logs.</li>
                  <li><strong>Timeline:</strong> We aim to delete or anonymise eligible data within <strong>2 working days</strong> of request verification.</li>
                  <li><strong>Third-Party Records:</strong> Information already viewed or downloaded by an employer may remain in their records per their policies.</li>
                </ul>
                <div className="mt-3 pt-3 border-top text-muted fs-13">
                  Prefer email? You can also email us directly at <a href="mailto:hr@webvi.in" className="text-primary fw-semibold">hr@webvi.in</a> with the subject <em>“Delete my Webvi account”</em>.
                </div>
              </div>
            </Col>

            {/* RIGHT SIDE: Deletion Request Form */}
            <Col lg={6} className="ms-auto mt-4 mt-lg-0">
              <div className="section-title">
                <h3 className="title text-danger">Webvi Account Deletion Request</h3>
                <p className="text-muted">
                  Please fill out the form below to request the permanent closure and removal of your Webvi account and associated data.
                </p>

                <Form
                  method="post"
                  className="contact-form mt-4"
                  onSubmit={onSubmit}
                  id="deleteForm"
                >
                  <Row>
                    <Col lg={12}>
                      <div className="mb-3">
                        <Label htmlFor="name" className="form-label fw-semibold">Full Name</Label>
                        <Input type="text" name="name" id="name" className="form-control" placeholder="Enter your full name" value={formData.name} onChange={onChangeValue} />
                      </div>
                    </Col>
                    
                    <Col md={6}>
                      <div className="mb-3">
                        <Label htmlFor="emailAddress" className="form-label fw-semibold">Registered Email</Label>
                        <Input type="email" className="form-control" id="emailAddress" name="emailAddress" placeholder="Enter registered email" value={formData.emailAddress} onChange={onChangeValue} />
                      </div>
                    </Col>

                    <Col md={6}>
                      <div className="mb-3">
                        <Label htmlFor="contactNumber" className="form-label fw-semibold">Registered Mobile Number</Label>
                        <Input type="text" className="form-control" id="contactNumber" name="contactNumber" placeholder="Enter mobile number" value={formData.contactNumber} onChange={onNumberInputChange} maxLength={10} />
                      </div>
                    </Col>

                    <Col md={12}>
                      <div className="mb-3">
                        <Label htmlFor="subject" className="form-label fw-semibold">Subject</Label>
                        <Input type="text" className="form-control bg-light" name="subject" value={formData.subject} readOnly disabled />
                      </div>
                    </Col>

                    <Col lg={12}>
                      <div className="mb-3">
                        <Label htmlFor="message" className="form-label fw-semibold">Reason for Deletion (Optional)</Label>
                        <textarea className="form-control" placeholder="Let us know why you are deleting your account..." name="message" rows="3" value={formData.message} onChange={onChangeValue}></textarea>
                      </div>
                    </Col>
                  </Row>

                  <div className="text-end">
                    <button className={`btn btn-danger px-4 ${loading ? 'disabled' : ''}`} type="submit">
                      {loading ? <>Processing... <Spinner size="sm" /></> : <>Request Deletion <i className="uil uil-trash-alt ms-1"></i></>}
                    </button>
                  </div>
                </Form>
              </div>
            </Col>

          </Row>
        </Container>
      </section>
    </React.Fragment>
  );
};

export default DeleteAccount;